import { Dropdown } from "grabcad-ui-elements";
import { useContext } from "react";
import { CompanyVersionChangeIndicator } from "./CompanyVersionChangeIndicator";
import {
    Company,
    NextVersion,
    SoftwareTagWithUpdateStrategy,
} from "../types/version_management_types";
import { ReleaseFrequency } from "@grabcad/company-server-shared/dist/types";
import styled from "styled-components";
import { ApplicationContext } from "../../../components/ApplicationProvider";
import { useTagDropDownOptions } from "../hooks/useTagDropDownOptions";

interface VersionManagementTableProps {
    company: Company;
    softwareTags: SoftwareTagWithUpdateStrategy[];
    onChange: (value: undefined | NextVersion) => void;
    isReadOnly?: boolean;
}

export function VersionManagementCompanyTable({
    company,
    softwareTags,
    onChange,
    isReadOnly,
}: VersionManagementTableProps) {
    const { t } = useContext(ApplicationContext);

    const dropdownOptions = useTagDropDownOptions(softwareTags);

    const handleTagChange = (tagId: number) => {
        if (isReadOnly) {
            return;
        }

        const tag = softwareTags.find((x) => x.id === tagId);
        if (tag) {
            onChange({ updateStrategy: tag.updateStrategy, tagId: tag.id });
        }
    };

    let selectedTagId = company.nextVersion ? company.nextVersion.tagId : company.currentTagId;
    const selectedTag = softwareTags.find((x) => x.id === selectedTagId);

    return (
        <>
            <table className="ui table" aria-label="Company">
                <thead>
                    <tr>
                        <th>{t("version_management.table_headings.company_name")}</th>
                        <th>{t("version_management.table_headings.stream")}</th>
                        <th>{t("version_management.table_headings.version")}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="five wide">{company.name}</td>
                        <td className="six wide">
                            <Dropdown
                                options={dropdownOptions}
                                value={selectedTagId}
                                fluid
                                onChange={(event, data) => {
                                    handleTagChange(data.value as number);
                                }}
                            />
                        </td>
                        <td className="five wide right aligned">
                            <CompanyVersionChangeIndicator
                                currentVersion={company.currentPrintVersion}
                                currentTagId={company.currentTagId}
                                nextVersion={company.nextVersion ?? undefined}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            <ReceivedVersionText role="status" aria-live="polite">
                {selectedTag?.releaseFrequency === ReleaseFrequency.MONTHLY
                    ? t("version_management.received_version_monthly")
                    : t("version_management.received_version_when_available")}
            </ReceivedVersionText>
        </>
    );
}

const ReceivedVersionText = styled.div`
    margin: -6px 0 0 12px;
    color: #6a6a6a;
`;
